import {
  type AppError,
  type ErrorHandlerOptions,
  type ErrorServiceConfig,
  type NetworkError,
  ErrorType,
  ErrorSeverity,
  HTTP_ERROR_MESSAGES,
  USER_ERROR_MESSAGES
} from '../types/error';
import { type AxiosError } from 'axios';

class ErrorService {
  private errors: AppError[] = [];
  private config: ErrorServiceConfig = {
    logToConsole: true,
    showUserNotifications: true,
    maxStoredErrors: 100,
  };
  private errorListeners: Array<(error: AppError) => void> = [];
  private successListeners: Array<(message: string) => void> = [];

  constructor() {
    // Listen for unhandled promise rejections
    if (typeof window !== 'undefined') {
      window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));
      window.addEventListener('error', this.handleWindowError.bind(this));
    }
  }

  /**
   * Configure the error service
   */
  configure(config: Partial<ErrorServiceConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Handle any error and convert it to AppError format
   */
  handleError(
    error: Error | AxiosError | string | unknown,
    options: ErrorHandlerOptions = {}
  ): AppError {
    const appError = this.convertToAppError(error, options);

    // Store the error
    this.storeError(appError);

    // Log to console if enabled
    if (this.config.logToConsole && (options.logToConsole !== false)) {
      this.logError(appError);
    }

    // Notify listeners
    this.notifyListeners(appError);

    return appError;
  }

  /**
   * Handle network/HTTP errors specifically
   */
  handleNetworkError(
    axiosError: AxiosError,
    options: ErrorHandlerOptions = {}
  ): NetworkError {
    const status = axiosError.response?.status;
    const statusText = axiosError.response?.statusText;
    const url = axiosError.config?.url;
    const responseData = axiosError.response?.data;

    // Extract server error message from response data
    const serverMessage = this.extractServerMessage(responseData);

    const networkError: NetworkError = {
      id: this.generateErrorId(),
      message: this.getNetworkErrorMessage(axiosError),
      type: this.getNetworkErrorType(status),
      severity: options.severity || this.getNetworkErrorSeverity(status),
      code: status,
      details: `${statusText} - ${url}`,
      timestamp: new Date(),
      // Use server message if available, otherwise fall back to generic message
      userMessage: options.userMessage || serverMessage || this.getUserFriendlyNetworkMessage(status),
      context: {
        url,
        status,
        statusText,
        method: axiosError.config?.method?.toUpperCase(),
        responseData,
        serverMessage,
        ...options.context
      },
      stack: axiosError.stack,
      originalError: axiosError,
      status,
      statusText,
      url,
    };

    return this.handleError(networkError, options) as NetworkError;
  }

  /**
   * Extract error message from server response data
   */
  private extractServerMessage(responseData: unknown): string | null {
    if (!responseData) return null;

    // If it's a string, use it directly
    if (typeof responseData === 'string') {
      return responseData;
    }

    // If it's an object, try common error message properties
    if (typeof responseData === 'object') {
      const data = responseData as Record<string, unknown>;
      const parts: string[] = [];

      // Title / main message
      if (typeof data.title === 'string') parts.push(data.title);
      else if (typeof data.message === 'string') parts.push(data.message);
      else if (typeof data.error === 'string') parts.push(data.error);
      else if (typeof data.errorMessage === 'string') parts.push(data.errorMessage);

      // Detail
      if (typeof data.detail === 'string') parts.push(data.detail);

      // Validation errors - show ALL of them
      if (data.errors && typeof data.errors === 'object') {
        const errors = data.errors as Record<string, string[]>;
        const errorLines = Object.entries(errors).map(([field, msgs]) => {
          const fieldName = field.replace(/^\$\./, '');
          return `${fieldName}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`;
        });
        if (errorLines.length > 0) {
          parts.push(errorLines.join(' | '));
        }
      }

      // ExceptionMessage from ASP.NET
      if (typeof data.exceptionMessage === 'string') parts.push(data.exceptionMessage);
      if (typeof data.stackTrace === 'string') parts.push(data.stackTrace.substring(0, 500));

      if (parts.length > 0) return parts.join('\n');
    }

    return null;
  }

  /**
   * Handle validation errors
   */
  handleValidationError(
    message: string,
    field?: string,
    options: ErrorHandlerOptions = {}
  ): AppError {
    const validationError: AppError = {
      id: this.generateErrorId(),
      message,
      type: ErrorType.VALIDATION,
      severity: options.severity || ErrorSeverity.MEDIUM,
      timestamp: new Date(),
      userMessage: options.userMessage || message,
      context: { field, ...options.context },
    };

    return this.handleError(validationError, options);
  }

  /**
   * Handle authentication errors
   */
  handleAuthError(
    message: string,
    options: ErrorHandlerOptions = {}
  ): AppError {
    const authError: AppError = {
      id: this.generateErrorId(),
      message,
      type: ErrorType.AUTHENTICATION,
      severity: options.severity || ErrorSeverity.HIGH,
      timestamp: new Date(),
      userMessage: options.userMessage || USER_ERROR_MESSAGES[ErrorType.AUTHENTICATION],
      context: options.context,
    };

    return this.handleError(authError, options);
  }

  /**
   * Get all stored errors
   */
  getErrors(): AppError[] {
    return [...this.errors];
  }

  /**
   * Get errors by type
   */
  getErrorsByType(type: ErrorType): AppError[] {
    return this.errors.filter(error => error.type === type);
  }

  /**
   * Get recent errors (last hour)
   */
  getRecentErrors(): AppError[] {
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
    return this.errors.filter(error => error.timestamp > oneHourAgo);
  }

  /**
   * Clear all errors
   */
  clearErrors(): void {
    this.errors = [];
  }

  /**
   * Clear error by ID
   */
  clearError(errorId: string): void {
    this.errors = this.errors.filter(error => error.id !== errorId);
  }

  /**
   * Show a success notification (green snackbar)
   */
  showSuccess(message: string): void {
    this.successListeners.forEach(listener => {
      try {
        listener(message);
      } catch (err) {
        console.error('Error in success listener:', err);
      }
    });
  }

  /**
   * Subscribe to error events
   */
  onError(listener: (error: AppError) => void): () => void {
    this.errorListeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.errorListeners.indexOf(listener);
      if (index > -1) {
        this.errorListeners.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to success events
   */
  onSuccess(listener: (message: string) => void): () => void {
    this.successListeners.push(listener);

    return () => {
      const index = this.successListeners.indexOf(listener);
      if (index > -1) {
        this.successListeners.splice(index, 1);
      }
    };
  }

  /**
   * Convert any error to AppError format
   */
  private convertToAppError(
    error: Error | AxiosError | string | unknown,
    options: ErrorHandlerOptions
  ): AppError {
    // If it's already an AppError, return it
    if (this.isAppError(error)) {
      return error;
    }

    // Handle axios errors
    if (this.isAxiosError(error)) {
      return this.handleNetworkError(error, options);
    }

    // Handle regular errors
    if (error instanceof Error) {
      return {
        id: this.generateErrorId(),
        message: error.message,
        type: ErrorType.UNKNOWN,
        severity: options.severity || ErrorSeverity.MEDIUM,
        timestamp: new Date(),
        userMessage: options.userMessage || 'An unexpected error occurred',
        context: options.context,
        stack: error.stack,
        originalError: error,
      };
    }

    // Handle string errors
    if (typeof error === 'string') {
      return {
        id: this.generateErrorId(),
        message: error,
        type: ErrorType.UNKNOWN,
        severity: options.severity || ErrorSeverity.LOW,
        timestamp: new Date(),
        userMessage: options.userMessage || error,
        context: options.context,
      };
    }

    // Handle unknown errors
    return {
      id: this.generateErrorId(),
      message: 'Unknown error occurred',
      type: ErrorType.UNKNOWN,
      severity: options.severity || ErrorSeverity.LOW,
      timestamp: new Date(),
      userMessage: options.userMessage || 'An unexpected error occurred',
      context: { originalError: error, ...options.context },
    };
  }

  private storeError(error: AppError): void {
    this.errors.push(error);

    // Limit stored errors
    if (this.errors.length > this.config.maxStoredErrors) {
      this.errors = this.errors.slice(-this.config.maxStoredErrors);
    }
  }

  private logError(error: AppError): void {
    const logLevel = this.getLogLevel(error.severity);
    const logMessage = `[${error.type.toUpperCase()}] ${error.message}`;

    console[logLevel](logMessage, {
      id: error.id,
      severity: error.severity,
      timestamp: error.timestamp,
      context: error.context,
      stack: error.stack,
    });
  }

  private notifyListeners(error: AppError): void {
    this.errorListeners.forEach(listener => {
      try {
        listener(error);
      } catch (listenerError) {
        console.error('Error in error listener:', listenerError);
      }
    });
  }

  private generateErrorId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getNetworkErrorMessage(error: AxiosError): string {
    if (error.response) {
      return `HTTP ${error.response.status}: ${error.response.statusText}`;
    }
    if (error.request) {
      return 'Network request failed - no response received';
    }
    return error.message || 'Network error occurred';
  }

  private getNetworkErrorType(status?: number): ErrorType {
    if (!status) return ErrorType.NETWORK;

    if (status === 401) return ErrorType.AUTHENTICATION;
    if (status === 403) return ErrorType.AUTHORIZATION;
    if (status === 404) return ErrorType.NOT_FOUND;
    if (status >= 400 && status < 500) return ErrorType.CLIENT_ERROR;
    if (status >= 500) return ErrorType.SERVER_ERROR;

    return ErrorType.NETWORK;
  }

  private getNetworkErrorSeverity(status?: number): ErrorSeverity {
    if (!status) return ErrorSeverity.MEDIUM;

    if (status >= 500) return ErrorSeverity.HIGH;
    if (status === 401 || status === 403) return ErrorSeverity.HIGH;
    if (status >= 400) return ErrorSeverity.MEDIUM;

    return ErrorSeverity.LOW;
  }

  private getUserFriendlyNetworkMessage(status?: number): string {
    if (status && HTTP_ERROR_MESSAGES[status]) {
      return HTTP_ERROR_MESSAGES[status];
    }
    return USER_ERROR_MESSAGES[ErrorType.NETWORK];
  }

  private getLogLevel(severity: ErrorSeverity): 'error' | 'warn' | 'info' {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
      case ErrorSeverity.HIGH:
        return 'error';
      case ErrorSeverity.MEDIUM:
        return 'warn';
      case ErrorSeverity.LOW:
      default:
        return 'info';
    }
  }

  private isAppError(error: unknown): error is AppError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'id' in error &&
      'message' in error &&
      'type' in error &&
      'severity' in error &&
      'timestamp' in error
    );
  }

  private isAxiosError(error: unknown): error is AxiosError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'isAxiosError' in error &&
      (error as any).isAxiosError === true
    );
  }

  private handleUnhandledRejection(event: PromiseRejectionEvent): void {
    this.handleError(event.reason, {
      severity: ErrorSeverity.HIGH,
      context: { source: 'unhandledRejection' },
      userMessage: 'An unexpected error occurred',
    });

    // Prevent the default browser handling
    event.preventDefault();
  }

  private handleWindowError(event: ErrorEvent): void {
    this.handleError(event.error || event.message, {
      severity: ErrorSeverity.HIGH,
      context: {
        source: 'windowError',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
      userMessage: 'An unexpected error occurred',
    });
  }
}

// Export singleton instance
export const errorService = new ErrorService();
export { ErrorService };

/**
 * Extract a detailed error message from any error type (AppError, AxiosError, Error, string).
 * Use this in catch blocks instead of `error instanceof Error ? error.message : 'generic'`
 */
export function getErrorMessage(error: unknown, fallback = 'Error desconocido'): string {
  if (!error) return fallback;

  // AppError from our error service (has userMessage with server details)
  if (typeof error === 'object' && error !== null) {
    const e = error as Record<string, any>;

    // AppError / NetworkError
    if (e.userMessage) {
      const parts = [e.userMessage];
      if (e.context?.serverMessage && e.context.serverMessage !== e.userMessage) {
        parts.push(e.context.serverMessage);
      }
      if (e.code) parts.unshift(`[HTTP ${e.code}]`);
      return parts.join(' - ');
    }

    // Axios error (not yet processed)
    if (e.isAxiosError && e.response?.data) {
      const data = e.response.data;
      if (typeof data === 'string') return `[HTTP ${e.response.status}] ${data}`;
      if (data.title) return `[HTTP ${e.response.status}] ${data.title}${data.detail ? ': ' + data.detail : ''}`;
      if (data.message) return `[HTTP ${e.response.status}] ${data.message}`;
      return `[HTTP ${e.response.status}] ${e.response.statusText}`;
    }

    // Regular Error
    if (e.message && typeof e.message === 'string') return e.message;
  }

  if (typeof error === 'string') return error;

  return fallback;
}